import { QRCodeSVG } from 'qrcode.react'
import { generateDigitalLinkUri } from '@esf/shared'

// Get the public URL — use custom domain in production, window.location for local dev
const getPublicUrl = (): string => {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return window.location.origin
  }
  return 'https://demo-dpp.ubloquity.net'
}

// Extract domain from URL for GS1 Digital Link
const getDomainFromUrl = (url: string): string => {
  try {
    return new URL(url).host
  } catch {
    return 'demo-dpp.ubloquity.net'
  }
}

interface QRCodeGeneratorProps {
  passportId: string
  gtin?: string
  batchNumber?: string
  serialNumber?: string
  baseUrl?: string
  size?: number
  showLabel?: boolean
  logoUrl?: string
}

export default function QRCodeGenerator({
  passportId,
  gtin,
  batchNumber,
  serialNumber,
  baseUrl,
  size = 200,
  showLabel = true,
}: QRCodeGeneratorProps) {
  const publicUrl = baseUrl || getPublicUrl()
  const domain = getDomainFromUrl(publicUrl)

  // Generate the URL - prefer GS1 Digital Link if GTIN available
  const url = gtin
    ? generateDigitalLinkUri(domain, gtin, batchNumber, serialNumber)
    : `${publicUrl}/#/public/passport/${passportId}`

  return (
    <div className="inline-flex flex-col items-center">
      <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
        <QRCodeSVG
          value={url}
          size={size}
          level="M"
          marginSize={2}
          bgColor="#FFFFFF"
          fgColor="#013C7A"
        />
      </div>
      {showLabel && (
        <div className="mt-2 text-center">
          <p className="text-xs font-medium text-gray-700">
            {gtin ? 'GS1 Digital Link' : 'Digital Passport'}
          </p>
          <p className="text-xs text-gray-500 font-mono">
            {gtin || passportId}
          </p>
        </div>
      )}
    </div>
  )
}

// Print-friendly label component
interface QRCodeLabelProps extends QRCodeGeneratorProps {
  productName: string
  labelSize?: 'small' | 'medium' | 'large'
}

export function QRCodeLabel({
  passportId,
  gtin,
  batchNumber,
  serialNumber,
  baseUrl,
  productName,
  labelSize = 'medium',
}: QRCodeLabelProps) {
  const publicUrl = baseUrl || getPublicUrl()
  const domain = getDomainFromUrl(publicUrl)

  const sizes = {
    small: { qr: 80, container: 'w-32', text: 'text-[8px]' },
    medium: { qr: 120, container: 'w-48', text: 'text-[10px]' },
    large: { qr: 180, container: 'w-72', text: 'text-xs' },
  }

  const config = sizes[labelSize]
  const url = gtin
    ? generateDigitalLinkUri(domain, gtin, batchNumber, serialNumber)
    : `${publicUrl}/#/public/passport/${passportId}`

  return (
    <div className={`${config.container} bg-white p-2 border border-gray-300 rounded print:border-black`}>
      <div className="flex flex-col items-center gap-1">
        {/* ESF Logo/Brand */}
        <p className={`${config.text} font-bold text-esf-navy uppercase`}>ESF Digital Passport</p>

        {/* QR Code */}
        <QRCodeSVG
          value={url}
          size={config.qr}
          level="M"
          marginSize={1}
          bgColor="#FFFFFF"
          fgColor="#000000"
        />

        {/* Product Info */}
        <div className="text-center w-full">
          <p className={`${config.text} font-medium text-gray-900 truncate`}>{productName}</p>
          {gtin && (
            <p className={`${config.text} text-gray-600 font-mono`}>GTIN: {gtin}</p>
          )}
          <p className={`${config.text} text-gray-500 font-mono`}>{passportId}</p>
        </div>

        {/* EU Compliance Mark */}
        <div className={`${config.text} flex items-center gap-1 text-gray-500`}>
          <span>EU ESPR Compliant</span>
        </div>
      </div>
    </div>
  )
}

// Batch print view for multiple labels
interface QRCodePrintSheetProps {
  products: Array<{
    passportId: string
    gtin?: string
    batchNumber?: string
    serialNumber?: string
    name: string
  }>
  baseUrl?: string
  labelSize?: 'small' | 'medium' | 'large'
  columns?: 2 | 3 | 4
}

export function QRCodePrintSheet({
  products,
  baseUrl,
  labelSize = 'medium',
  columns = 3,
}: QRCodePrintSheetProps) {
  const publicUrl = baseUrl || getPublicUrl()
  const gridCols = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  }

  return (
    <div className="bg-white p-4">
      {/* Print Header */}
      <div className="hidden print:block text-center mb-4">
        <h1 className="text-lg font-bold">ESF Digital Product Passport Labels</h1>
        <p className="text-sm text-gray-500">
          Generated: {new Date().toLocaleDateString()} | {products.length} labels
        </p>
      </div>

      {/* Labels Grid */}
      <div className={`grid ${gridCols[columns]} gap-4 print:gap-2`}>
        {products.map((product, index) => (
          <QRCodeLabel
            key={index}
            passportId={product.passportId}
            gtin={product.gtin}
            batchNumber={product.batchNumber}
            serialNumber={product.serialNumber}
            baseUrl={publicUrl}
            productName={product.name}
            labelSize={labelSize}
          />
        ))}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  )
}
